import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, Platform } from 'react-native';

import { Box, Text } from '@components/Restyle';
import Button from '@components/shared/Button/Button';
import Icon from '@components/shared/Icon/Icon';
import {
  ListChatsPaginationDocument,
  Task,
  useCreateUserRequestMutation,
  UserRequestable,
  UserRequestCategory,
} from '@graphql/generated';
import theme from '@themes/theme';

interface TaskInviteRequestButtonProps {
  currentUserHasPendingMemberAdditionRequest?: boolean;
  taskId?: Task['id'];
  onRequestSent?: () => void;
}

export const TaskInviteRequestButton: React.FC<
  TaskInviteRequestButtonProps
> = ({ currentUserHasPendingMemberAdditionRequest, taskId, onRequestSent }) => {
  const { t } = useTranslation('models');

  const [sendRequestToJoin, setSendRequestToJoin] = useState(
    currentUserHasPendingMemberAdditionRequest
  );
  const [loading, setLoading] = useState<boolean>(false);

  // The pending state can arrive after mount (e.g. from a separate query in
  // the task panel's no-access branch — TA-11014). Follow it to true, never
  // back: a request sent from this button must not be undone by a stale prop.
  if (currentUserHasPendingMemberAdditionRequest && !sendRequestToJoin) {
    setSendRequestToJoin(true);
  }

  const onRequestSentRef = useRef(onRequestSent);
  onRequestSentRef.current = onRequestSent;

  const [
    createUserRequest,
    { data: createUserRequestData, error: createUserRequestError },
  ] = useCreateUserRequestMutation({
    refetchQueries: [
      { query: ListChatsPaginationDocument, variables: { first: 20 } },
      'listMessages',
      'getTask',
      'getPublicTask',
    ],
  });

  useEffect(() => {
    if (!createUserRequestData) return;
    setLoading(false);
    setSendRequestToJoin(true);
    onRequestSentRef.current?.();
  }, [createUserRequestData]);

  useEffect(() => {
    if (!createUserRequestError) return;
    setLoading(false);
  }, [createUserRequestError]);
  const sendRequest = () => {
    setLoading(true);
    createUserRequest({
      variables: {
        attributes: {
          requestType: UserRequestCategory.MemberAddition,
          userRequestableId: taskId || '',
          userRequestableType: UserRequestable.Task,
        },
      },
    });
  };

  const btnText = !sendRequestToJoin
    ? t('models:projects.requestToJoin')
    : t('models:projects.pendingRequest');

  return (
    <Box
      style={{ width: '100%' }}
      flexDirection='column'
      alignItems='center'
      marginVertical={Platform.OS === 'web' ? 'listFooter' : 'xl'}>
      <Icon
        variant='l'
        color='textSecondary'
        name='AlertTriangle'
        width={Platform.OS === 'web' ? 40 : 28}
        height={Platform.OS === 'web' ? 40 : 28}
        marginBottom='xs'></Icon>
      <Text
        style={{ width: '100%', textAlign: 'center' }}
        variant={Platform.OS === 'web' ? 'heading2' : 'labelEmphasized'}
        lineHeight={Platform.OS === 'web' ? undefined : 21}
        color='textSecondary'
        marginBottom='m'>
        You’re not an assignee of this task
      </Text>
      <Box width={180} height={48}>
        <Button
          isSmall
          backgroundColor={!sendRequestToJoin && !loading ? 'black' : 'grey02'}
          prefix={
            <Icon
              variant='s'
              name='UserCheck'
              color={!sendRequestToJoin && !loading ? 'white' : 'grey04'}
            />
          }
          onPress={() => !sendRequestToJoin && !loading && sendRequest()}
          borderRadius='xl'
          variant={!sendRequestToJoin && !loading ? 'create' : 'pending'}
          prefixMarginRight='xs'
          disabled={sendRequestToJoin || loading}
          btnTextColor={!sendRequestToJoin && !loading ? 'white' : 'grey04'}
          accessibilityLabel={t('models:projects.requestToJoin')}>
          {loading ? (
            <Box ml='m'>
              <ActivityIndicator color={theme.colors.textPrimary} size={16} />
            </Box>
          ) : (
            btnText
          )}
        </Button>
      </Box>
    </Box>
  );
};
